import LoginRadiusSDK from 'libraries/loginRadius';
import { constants } from 'aliases';
import {
  Account,
  ForgotPasswordResponse,
  LoginRadiusError,
  QrAccessToken,
  QrCodeToken,
  SmartLoginSuccessResponse,
  SsoJWT,
  SsoJwtResponse,
  UserRegistrationByEmailResponse,
  UserSignUpByEmailResponse,
  AccessTokenResponse,
  InvalidateAccessTokenResponse,
} from 'types/loginRadius';
import { CheckEmailAvailabilityResponse } from 'types/loginRadius';
import LoginRadiusApiClient, {
  Endpoints,
  LrClientOptions,
} from './LoginRadiusApiClient';
import { HttpOptions } from './CwApiClient';
import { AppData, Router } from '@lightningjs/sdk';
import MParticleAnalytics from './analytics/MParticle';
import ConvivaAnalytics from './analytics/Conviva';
import PersonaService, { SottResponse } from 'services/personaService';
import jwtDecode from 'jwt-decode';
import {
  ErrorCode,
  reportError,
  reportLogin,
  reportLogout,
} from './analytics/reportingServicePage';
import { LoginType } from 'services/analytics/MParticle';
import { translate } from 'support/translate';
import AbstractLocalHistoryService from './history/local/AbstractLocalHistoryService';
import { getPageIdFromHash } from 'support/routerUtils';
import { PageId } from 'types/pageId';
import ApiServiceError from 'support/ApiServiceError';
import { getQrCodeUrl } from 'support/loginRadiusUtils';

type LoginRadiusParams = {
  apiKey: string;
};

export type QrCodeInfo = {
  accessToken: string;
  code: string;
  expiry: number;
  created: number;
  lastCheckedTimestamp: number;
};

const QR_TOKEN_TIME_TO_LIVE = 600;
const JWT_EXPIRY_BUFFER = 20; // seconds
const ACCESS_TOKEN_STORAGE_KEY = 'LRTokenKey';
const ACCESS_TOKEN_EXPIRY_KEY = 'LRTokenExpiryKey';
const CUSTOM_FIELD_CW_HAS_LOGGED_IN =
  constants.brandTokens.loginRadiusHasLoggedInField;
const CUSTOM_FIELD_CW_MARKETING_EMAILS =
  constants.brandTokens.loginRadiusMarketingEmailsField;

// 905 or 906 errors occur when the access token is invalid or expired (already signed out)
// 938 occurs when user does not exist
const INVALID_ACCESS_TOKEN_ERROR_CODES = [905, 906, 938];

const client = new LoginRadiusApiClient();

// Convince wrapper for Login Radius functions, automatically sets un-passed null args and passes promise resolution callback
const lrPromise = <T extends (...args: any[]) => any>(
  lrFn: T,
  ...args: Parameters<T>
): Promise<ReturnType<T>> =>
  new Promise((resolve, reject) => {
    const nonCallbackArgsLength = lrFn.length - 1;

    const nullArgs = Array<null>(nonCallbackArgsLength - args.length).fill(
      null,
    );
    try {
      lrFn(
        ...args,
        ...nullArgs,
        (error: LoginRadiusError, data: ReturnType<T>) => {
          error ? reject(error) : resolve(data);
        },
      );
    } catch (e) {
      reject(e);
    }
  });

export default class LoginRadiusService {
  static readonly serviceName = 'LoginRadiusService';

  private apiKey = '';
  private localHistoryService: AbstractLocalHistoryService;
  private account: Account | null = null;
  private sottToken?: string;
  private sottExpiry = 0;
  private ssoJwt?: string;
  private ssoJwtExpiry?: number;
  private currentLoginType?: LoginType;

  private mParticle!: MParticleAnalytics;
  private conviva!: ConvivaAnalytics;
  private personaService!: PersonaService;

  constructor(
    params: LoginRadiusParams,
    localHistoryService: AbstractLocalHistoryService,
  ) {
    const { apiKey } = params;

    this.apiKey = apiKey;
    this.localHistoryService = localHistoryService;
  }

  init(
    mParticle: MParticleAnalytics,
    conviva: ConvivaAnalytics,
    personaService: PersonaService,
    isProd: boolean,
  ) {
    // For list of common options, look at:
    // https://www.loginradius.com/docs/deployment/js-libraries/getting-started#initialization-of-loginradius-object
    const sdkOptions = {
      apiKey: this.apiKey,
      debugMode: !isProd,
    };

    this.mParticle = mParticle;
    this.conviva = conviva;
    this.personaService = personaService;

    this.checkTokenExpiration();

    LoginRadiusSDK.initSDK(sdkOptions);
  }

  async lrRequest<T extends (...args: any[]) => any>(
    lrFn: T,
    ...args: Parameters<T>
  ): Promise<ReturnType<T>> {
    try {
      return await lrPromise(lrFn, ...args);
    } catch (e) {
      const errorCode = (e as LoginRadiusError).ErrorCode;
      if (INVALID_ACCESS_TOKEN_ERROR_CODES.includes(errorCode)) {
        await this.setLogoutState();
      }
      throw e;
    }
  }

  /**
   * Check if currently logged in
   */
  isLoggedIn(): boolean {
    // It is possible that the access token could expire during a session but unlikely due to token refresh on boot
    return !!this.account && !!LoginRadiusSDK.getToken();
  }

  getAccount(): Account | null {
    return this.account;
  }

  hasAccessToken() {
    return !!LoginRadiusSDK.getToken();
  }

  async getLoginSott() {
    const utcTimestamp = Date.now();
    if (!this.sottToken || utcTimestamp > this.sottExpiry!) {
      const sottResp: SottResponse | undefined =
        await this.personaService.getLoginSott();
      if (sottResp) {
        this.sottToken = sottResp.Sott;
        this.sottExpiry = new Date(sottResp.Expiry).getTime();
      }
    }
    return this.sottToken ?? '';
  }

  // this function should only fetch a new JWT when the previous token expires, otherwise it should return a stored JWT
  async getSsoJwt(): Promise<string> {
    const unixTimestamp = Math.floor(Date.now() / 1000);
    const hasJwt = this.ssoJwt && this.ssoJwtExpiry;

    // we give a bit of buffer for the expiry so the JWT doesn't expire during the request that uses the JWT
    if (hasJwt && unixTimestamp < this.ssoJwtExpiry! - JWT_EXPIRY_BUFFER) {
      return this.ssoJwt!;
    }

    const options: LrClientOptions = {
      useSiteUrl: true,
      params: {
        apiKey: this.apiKey,
        access_token: LoginRadiusSDK.getToken(),
      },
    };

    try {
      const response = await client.request<SsoJwtResponse>(
        'GET',
        `api/jwt/${AppData?.config?.login.endpoints.login_radius_sso_token}/token`,
        options,
      );
      this.ssoJwt = response.data.signature;
      this.ssoJwtExpiry = jwtDecode<SsoJWT>(this.ssoJwt).exp;

      return this.ssoJwt;
    } catch (e) {
      const responseData = (e as ApiServiceError).response?.data;
      if (INVALID_ACCESS_TOKEN_ERROR_CODES.includes(responseData.ErrorCode)) {
        await this.setLogoutState();
      }
      return '';
    }
  }

  async checkEmailAvailability(email: string): Promise<boolean | undefined> {
    return new Promise((resolve, reject) => {
      LoginRadiusSDK.authenticationApi.checkEmailAvailability(
        email,
        (error: LoginRadiusError, data: CheckEmailAvailabilityResponse) => {
          error ? reject(error) : resolve(data.IsExist);
        },
      );
    });
  }

  async registerEmail(email: string, password: string, optIn = false) {
    // the API is fine with empty passwords, but we should prevent it
    if (password.length === 0) {
      throw {
        ErrorCode: 0,
        Message: translate('account.login.emptyPasswordError'),
        Description: translate('account.login.emptyPasswordError'),
      };
    }

    const authUserRegistrationModel = {
      email: [
        {
          type: 'Primary',
          value: email,
        },
      ],
      password,
      CustomFields: {
        [CUSTOM_FIELD_CW_MARKETING_EMAILS]: optIn ? 'true' : 'false',
        [CUSTOM_FIELD_CW_HAS_LOGGED_IN]: 'true',
      },
    };
    const sott = await this.getLoginSott();

    // Optional
    const emailTemplate = AppData?.login.emailTemplates.registration;
    const fields = null;
    const options = null;
    const verificationUrl = AppData?.config?.login.endpoints.web_login;
    const welcomeEmailTemplate = null;

    const result = await new Promise<UserRegistrationByEmailResponse>(
      (resolve, reject) =>
        LoginRadiusSDK.authenticationApi.userRegistrationByEmail(
          authUserRegistrationModel,
          sott,
          emailTemplate,
          fields,
          options,
          verificationUrl,
          welcomeEmailTemplate,
          (error: LoginRadiusError, data: UserRegistrationByEmailResponse) => {
            error ? reject(error) : resolve(data);
          },
        ),
    );

    const { access_token, expires_in, Profile } = result.Data;
    await this.setLoginState(access_token, expires_in, Profile);

    return result;
  }

  async generateQrCode(timeToLive = QR_TOKEN_TIME_TO_LIVE) {
    const options: HttpOptions = {
      params: {
        apiKey: this.apiKey,
        expiry: timeToLive,
      },
    };

    return client
      .request<QrCodeToken>('GET', Endpoints.GENERATE_QR_CODE_TOKEN, options)
      .then(response => {
        const now = Date.now();

        const info: QrCodeInfo = {
          code: response.data.code,
          lastCheckedTimestamp: now,
          expiry: now + timeToLive * 1000,
          created: now,
          accessToken: '',
        };

        return info;
      });
  }

  async getAccessTokenFromQrCode(code: string) {
    const options: HttpOptions = {
      params: {
        apiKey: this.apiKey,
        code,
      },
    };

    const result = await client
      .request<QrAccessToken>('GET', Endpoints.QR_ACCESS_TOKEN, options)
      .then(response => {
        return response.data;
      });

    if (!result.access_token) return;

    const { access_token } = result;
    await this.signInWithAccessToken(access_token, 'Other');

    return access_token;
  }

  async loginByEmail(email: string, password: string) {
    const emailAuthModel = {
      email,
      password,
    };

    // Optional
    const emailTemplate = null;
    const fields = null;
    const options = null;
    const verificationUrl = null;

    const result = await new Promise<UserSignUpByEmailResponse>(
      (resolve, reject) =>
        LoginRadiusSDK.authenticationApi.loginByEmail(
          emailAuthModel,
          emailTemplate,
          fields,
          options,
          verificationUrl,
          (error: LoginRadiusError, data: UserSignUpByEmailResponse) => {
            error ? reject(error) : resolve(data);
          },
        ),
    );

    const { access_token, expires_in, Profile } = result;
    await this.setLoginState(access_token, expires_in, Profile, 'Password');

    return result;
  }

  async updateCustomFieldsByAccessToken(customFields: object) {
    const token = LoginRadiusSDK.getToken();
    if (!token) {
      throw new Error('Access token is required');
    }

    const profileWithCustomFieldsOnly = {
      CustomFields: customFields,
    };

    const result = await this.lrRequest(
      LoginRadiusSDK.authenticationApi.updateProfileByAccessToken,
      token,
      profileWithCustomFieldsOnly,
    );

    const { IsPosted, Data } = result;
    // Refresh account state with updated profile
    if (IsPosted && Data) {
      this.account = this.getAccountData(Data);
    }

    return result;
  }

  async smartRegisterRequest(email: string, code: string, optIn: boolean) {
    const verificationUrl = getQrCodeUrl(code);
    if (!verificationUrl) return Promise.resolve(null);

    const authUserRegistrationModel = {
      email: [
        {
          type: 'Primary',
          value: email,
        },
      ],
      CustomFields: {
        [CUSTOM_FIELD_CW_MARKETING_EMAILS]: optIn ? 'true' : 'false',
        [CUSTOM_FIELD_CW_HAS_LOGGED_IN]: 'true',
      },
    };
    const emailTemplate = AppData?.login.emailTemplates.registration;

    const sott = await this.getLoginSott();

    return this.lrRequest(
      (LoginRadiusSDK.authenticationApi as any).userRegistrationByEmail,
      authUserRegistrationModel,
      sott,
      emailTemplate,
      null,
      null,
      verificationUrl,
    );
  }

  async smartLoginRequest(guid: string, email: string) {
    return this.lrRequest(
      (LoginRadiusSDK.smartLoginApi as any).smartLoginByEmail,
      guid,
      email,
      null,
      AppData?.login.emailTemplates.smartLogin,
    );
  }

  async smartLoginPing(
    guid: string,
  ): Promise<SmartLoginSuccessResponse | null> {
    try {
      const result: SmartLoginSuccessResponse = await this.lrRequest(
        (LoginRadiusSDK.smartLoginApi as any).smartLoginPing,
        guid,
      );

      const { access_token, expires_in, Profile } = result;
      await this.setLoginState(access_token, expires_in, Profile, 'Magic Link');

      return result;
    } catch (e: any) {
      if ('ErrorCode' in e && e.ErrorCode === 1139) {
        // expected unverified error
        return null;
      }
      throw e;
    }
  }

  async forgotPassword(email: string) {
    const resetPasswordUrl = AppData?.config?.login.endpoints.web_login;
    // Optional
    const emailTemplate = AppData?.login.emailTemplates.forgotPassword;

    return new Promise((resolve, reject) =>
      LoginRadiusSDK.authenticationApi.forgotPassword(
        email,
        resetPasswordUrl,
        emailTemplate,
        (error: LoginRadiusError, data: ForgotPasswordResponse) => {
          error ? reject(error) : resolve(data.IsPosted);
        },
      ),
    );
  }

  async signOut() {
    const signOutPromise: Promise<InvalidateAccessTokenResponse> =
      this.lrRequest(
        LoginRadiusSDK.authenticationApi.authInValidateAccessToken,
        LoginRadiusSDK.getToken(),
      );

    let shouldSetLogoutState = false;
    try {
      const signOutData = await signOutPromise;
      shouldSetLogoutState = signOutData.IsPosted;
    } catch (error) {
      console.error('Failed to logout from Radius', error);
      const errorCode = (error as LoginRadiusError).ErrorCode;
      shouldSetLogoutState =
        INVALID_ACCESS_TOKEN_ERROR_CODES.includes(errorCode);
    }
    if (shouldSetLogoutState) await this.setLogoutState();
  }

  async autoLogin() {
    if (!LoginRadiusSDK.getToken()) return;

    try {
      const accountPromise: Promise<Account> = this.lrRequest(
        LoginRadiusSDK.authenticationApi.getProfileByAccessToken,
        LoginRadiusSDK.getToken(),
      );

      const tokenPromise: Promise<AccessTokenResponse> = this.lrRequest(
        LoginRadiusSDK.authenticationApi.authValidateAccessToken,
        LoginRadiusSDK.getToken(),
      );

      const [account, oldToken] = await Promise.all([
        accountPromise,
        tokenPromise,
      ]);

      const { access_token, expires_in } =
        await this.personaService.refreshAccessToken(oldToken.refresh_token);

      // Don't pass loginType to avoid firing login analytics on auto-login
      await this.setLoginState(access_token, expires_in, account);

      // But restore the persisted login type and profile for logout analytics
      const persistedLoginType = AppData?.storageService.loginType.get();

      if (persistedLoginType) {
        this.currentLoginType = persistedLoginType;
      }
    } catch (e: any) {
      if (INVALID_ACCESS_TOKEN_ERROR_CODES.includes(e.ErrorCode)) {
        // expired or invalided access token
        reportError({ error: e, userFacing: true, code: ErrorCode.c42 });
        await this.setLogoutState();
        // still throw error for user feedback
      }
      throw e;
    }
  }

  private async signInWithAccessToken(token: string, loginType?: LoginType) {
    const profilePromise: Promise<object> = this.lrRequest(
      LoginRadiusSDK.authenticationApi.getProfileByAccessToken,
      token,
    );
    const tokenDataPromise: Promise<AccessTokenResponse> = this.lrRequest(
      LoginRadiusSDK.authenticationApi.authValidateAccessToken,
      token,
    );

    const [profile, tokenData] = await Promise.all([
      profilePromise,
      tokenDataPromise,
    ]);

    const { access_token, expires_in } = tokenData;
    await this.setLoginState(access_token, expires_in, profile, loginType);
  }

  private checkTokenExpiration(): void {
    const expiresStr = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
    if (!expiresStr) return;

    const expiresDate = new Date(expiresStr);
    const now = new Date();

    if (now >= expiresDate) {
      localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
      localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
    }
  }

  private async setLoginState(
    accessToken: string,
    expiry: string,
    profileObj: object,
    loginType?: LoginType,
  ) {
    const expires = new Date(expiry).toUTCString();
    localStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, accessToken);
    localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, expires);
    this.account = this.getAccountData(profileObj);

    this.currentLoginType = loginType;

    // Persist login type to localStorage for auto-login scenarios
    if (loginType) {
      AppData?.storageService.loginType.set(loginType);
    }

    // Check if custom fields have 'cw-has-logged-in' key set to 'true' if not set it to 'true'
    if (this.account?.customFields[CUSTOM_FIELD_CW_HAS_LOGGED_IN] !== 'true') {
      try {
        await this.updateCustomFieldsByAccessToken({
          [CUSTOM_FIELD_CW_HAS_LOGGED_IN]: 'true',
        });
      } catch (error) {
        // Error is silently ignored as this should not block the login flow
      }
    }

    await this.syncAccountData();

    AppData?.oneTrustService?.setUser(this.account?.uid ?? null);
  }

  private async setLogoutState() {
    // SDK does not remove access token from cookie, so we need to do it manually
    document.cookie = `${ACCESS_TOKEN_STORAGE_KEY}=; Secure; SameSite=Strict; Path=/; Max-Age=0`;

    // Clear keys from localStorage
    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);

    await this.unsyncAccountData();

    // unsyncAccountData still needs the account for logout analytics, clear it after unsyncing
    this.account = null;
    this.sottToken = undefined;
    this.sottExpiry = 0;
    this.ssoJwt = undefined;
    this.ssoJwtExpiry = 0;
    this.currentLoginType = undefined;

    AppData?.oneTrustService?.setUser(null);
  }

  private getAccountData(profile: object): Account {
    const uid = (profile as any).Uid;
    const primaryEmail = (profile as any).Email.find(
      (email: any) => email.Type === 'Primary',
    ).Value;
    const customFields = (profile as any).CustomFields || {};
    return { uid, primaryEmail, customFields };
  }

  private async loginAnalytics() {
    if (!this.account) return;

    await this.mParticle?.login(this.account);
    this.conviva?.updateUser();

    if (this.currentLoginType && this.account) {
      reportLogin({
        loginType: this.currentLoginType,
        uid: this.account.uid,
        primaryEmail: this.account.primaryEmail,
        pageId: getPageIdFromHash(Router.getActiveHash() ?? '') as PageId,
      });
    }
  }

  private async syncAccountData() {
    if (!this.account) return;

    if (this.localHistoryService.hasLoggedOutItems()) {
      try {
        const videos = await this.localHistoryService.getAllWatchHistoryItems();
        const lastWatchedGuids =
          this.localHistoryService.getSortedWatchHistoryEpisodeGuids(false);
        await this.personaService.batchLoadContinueWatchingItems(
          videos,
          lastWatchedGuids,
        );

        this.localHistoryService.clearHistory();
      } catch (e) {
        // report error and continue, history sync will attempt again on next boot
        reportError({ error: e, userFacing: false, code: ErrorCode.c41 });
      }
    }

    await this.loginAnalytics();
  }

  private async logoutAnalytics() {
    await this.mParticle?.logout();
    this.conviva?.updateUser();

    if (this.currentLoginType && this.account) {
      reportLogout({
        loginType: this.currentLoginType,
        uid: this.account.uid,
        pageId: getPageIdFromHash(Router.getActiveHash() ?? '') as PageId,
      });
    }

    // Clear stored values after logout
    this.currentLoginType = undefined;
    // Clear persisted login type
    AppData?.storageService.loginType.remove();
  }

  private async unsyncAccountData() {
    // always clear the history, in most cases it should already be empty
    this.localHistoryService.clearHistory();

    await this.logoutAnalytics();
  }
}
